import { useCallback } from 'react';
import { 
  NODE_WIDTH, NODE_HEIGHT, SPACING_PADDING, LAYOUT_PADDING,
  HORIZONTAL_GAP, VERTICAL_GAP 
} from './constants';
import { 
  buildPositionMap, getAllPositions, createOverlapChecker,
  SPIRAL_DIRECTIONS
} from './positionHelpers';

/**
 * Hook for finding empty space for new nodes
 */
export function useFindEmptySpace({
  tasks,
  nodePositions,
  nodeDimensions,
  customPositionsRef,
  getViewportCenter,
  getViewportBounds,
}) {
  /**
   * Find empty space in the visible viewport for a new node
   */
  const findEmptySpaceInViewport = useCallback(() => {
    const viewport = getViewportBounds();
    const center = getViewportCenter();
    const positionMap = buildPositionMap(nodePositions, customPositionsRef);
    const allPositions = getAllPositions(tasks, positionMap, nodeDimensions);
    
    const centerPos = { x: center.x - NODE_WIDTH / 2, y: center.y - NODE_HEIGHT / 2 };
    
    // If no existing nodes, place at tidy-up starting position
    if (allPositions.length === 0) {
      return { x: LAYOUT_PADDING, y: LAYOUT_PADDING };
    }
    
    const overlapsWithNode = createOverlapChecker(allPositions, SPACING_PADDING);
    
    const isInViewport = (x, y) => {
      const margin = 50;
      return x >= viewport.left + margin && 
             x + NODE_WIDTH <= viewport.right - margin &&
             y >= viewport.top + margin && 
             y + NODE_HEIGHT <= viewport.bottom - margin;
    };
    
    // Try center first
    if (!overlapsWithNode(centerPos.x, centerPos.y)) {
      return centerPos;
    }
    
    // Spiral outward from center
    const STEP = 60;
    const MAX_RINGS = 15;
    
    for (let ring = 1; ring <= MAX_RINGS; ring++) {
      const distance = ring * STEP;
      for (const dir of SPIRAL_DIRECTIONS) {
        const x = centerPos.x + dir.dx * distance;
        const y = centerPos.y + dir.dy * distance;
        if (isInViewport(x, y) && !overlapsWithNode(x, y)) {
          return { x, y };
        }
      }
    }
    
    // Fallback: center even if overlapping
    return centerPos;
  }, [tasks, nodePositions, nodeDimensions, customPositionsRef, getViewportCenter, getViewportBounds]);

  /**
   * Find empty space near a specific source node (for connected nodes)
   * 
   * Positions the new node like the "tidy" algorithm would:
   * - Centered below the parent if no siblings exist
   * - To the right of existing siblings if they exist (same row)
   * - Maintains proper spacing from the layout constants
   */
  const findEmptySpaceNearNode = useCallback((sourceNodeId, options = {}) => {
    const positionMap = buildPositionMap(nodePositions, customPositionsRef);
    const sourcePos = positionMap[sourceNodeId];
    
    if (!sourcePos) {
      return findEmptySpaceInViewport();
    }
    
    const sourceDims = nodeDimensions[sourceNodeId] || { width: NODE_WIDTH, height: NODE_HEIGHT };

    // (+) should release the card from the handle, then tidy slides it into the slot.
    if (options.atHandle) {
      const below = options.side !== 'before';
      return {
        x: sourcePos.x,
        y: below
          ? sourcePos.y + Math.max(sourceDims.height - 28, 28)
          : sourcePos.y - 28,
      };
    }
    
    // Find existing children of the source node (these will be siblings of the new node)
    const existingSiblings = tasks.filter(task => {
      if (!task.dependsOn) return false;
      const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
      return deps.some(dep => {
        const depId = typeof dep === 'string' ? dep : (dep?.taskId || dep?.nodeId);
        return depId === sourceNodeId;
      });
    });
    
    // Calculate the Y position for the child layer (below parent)
    const belowY = sourcePos.y + sourceDims.height + VERTICAL_GAP;
    
    if (existingSiblings.length === 0) {
      // No existing siblings - center the new node below the parent
      const parentCenterX = sourcePos.x + sourceDims.width / 2;
      const centeredX = parentCenterX - NODE_WIDTH / 2;
      return { x: centeredX, y: belowY };
    }
    
    // There are existing siblings - find their positions and place new node to the right
    const siblingPositions = existingSiblings
      .map(sibling => {
        const pos = positionMap[sibling.id];
        const dims = nodeDimensions[sibling.id] || { width: NODE_WIDTH, height: NODE_HEIGHT };
        return pos ? { ...pos, width: dims.width, height: dims.height, id: sibling.id } : null;
      })
      .filter(Boolean);
    
    if (siblingPositions.length === 0) {
      // Siblings exist but don't have positions yet - center below parent
      const parentCenterX = sourcePos.x + sourceDims.width / 2;
      const centeredX = parentCenterX - NODE_WIDTH / 2;
      return { x: centeredX, y: belowY };
    }
    
    // Find the rightmost sibling at the same Y level (or close to it)
    // Siblings should be in the same layer (similar Y position)
    const siblingY = siblingPositions[0].y;
    const siblingsInSameRow = siblingPositions.filter(
      sib => Math.abs(sib.y - siblingY) < 20 // Allow small variance
    );
    
    if (siblingsInSameRow.length > 0) {
      // Place to the right of the rightmost sibling in the same row
      const rightmostSibling = siblingsInSameRow.reduce((rightmost, current) => {
        const currentRight = current.x + current.width;
        const rightmostRight = rightmost.x + rightmost.width;
        return currentRight > rightmostRight ? current : rightmost;
      });
      
      const newX = rightmostSibling.x + rightmostSibling.width + HORIZONTAL_GAP;
      return { x: newX, y: siblingY };
    }
    
    // Fallback: place below parent, centered
    const parentCenterX = sourcePos.x + sourceDims.width / 2;
    const centeredX = parentCenterX - NODE_WIDTH / 2;
    return { x: centeredX, y: belowY };
  }, [tasks, nodePositions, nodeDimensions, customPositionsRef, findEmptySpaceInViewport]);

  /**
   * Find empty space near a specific point (for inserting on edges)
   */
  const findEmptySpaceNearPoint = useCallback((point) => {
    const positionMap = buildPositionMap(nodePositions, customPositionsRef);
    const allPositions = getAllPositions(tasks, positionMap, nodeDimensions);
    const overlapsWithNode = createOverlapChecker(allPositions, 40);
    
    const centeredX = point.x - NODE_WIDTH / 2;
    const centeredY = point.y - NODE_HEIGHT / 2;
    
    if (!overlapsWithNode(centeredX, centeredY)) {
      return { x: centeredX, y: centeredY };
    }
    
    const STEP = 60;
    const MAX_RINGS = 10;
    
    const directions = [
      { dx: 0, dy: 1 }, { dx: 1, dy: 0 }, { dx: 0, dy: -1 }, { dx: -1, dy: 0 },
      { dx: 1, dy: 1 }, { dx: -1, dy: 1 }, { dx: 1, dy: -1 }, { dx: -1, dy: -1 },
    ];
    
    for (let ring = 1; ring <= MAX_RINGS; ring++) {
      const distance = ring * STEP;
      for (const dir of directions) {
        const x = centeredX + dir.dx * distance;
        const y = centeredY + dir.dy * distance;
        if (!overlapsWithNode(x, y)) {
          return { x, y };
        }
      }
    }
    
    return { x: centeredX, y: centeredY };
  }, [tasks, nodePositions, nodeDimensions, customPositionsRef]);

  return {
    findEmptySpaceInViewport,
    findEmptySpaceNearNode,
    findEmptySpaceNearPoint,
  };
}
